/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flatten from 'lodash.flatten';
import { computed, transaction } from 'mobx';
import { Observer, observer } from 'mobx-react-lite';
import * as React from 'react';
import { Prompt } from 'react-router';
import { confirmLeave } from 'src/core/components/ConfirmLeaveModal';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LimitedAccessToFeaturesMessageBar } from 'src/core/components/LimitedAccessToFeaturesMessageBar';
import { Page } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useImperativeProgressTracker } from 'src/core/hooks/useImperativeProgressTracker';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useSelection } from 'src/core/hooks/useSelection';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { noop } from 'src/core/utils/base';
import { globalMessages } from 'src/core/utils/globalMessages';
import { reviewEndpointMessages } from 'src/luis/appShell/buildPane/reviewEndpointPage/reviewEndpointMessages';
import { ReviewEndpointUtterancesUIStore } from 'src/luis/appShell/buildPane/reviewEndpointPage/ReviewEndpointUtterancesUIStore';
import { useReviewEndpointLocalLabelingActions } from 'src/luis/appShell/buildPane/reviewEndpointPage/useReviewEndpointLocalLabelingActions';
import { useIntents } from 'src/luis/modules/intents/useIntents';
import { useEntities } from 'src/luis/projects/entities/hooks/useEntities';
import { EntityRole } from 'src/luis/projects/entities/types/EntityRole';
import { isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { EntityDropdownContainer } from 'src/luis/projects/utterances/components/EntityDropdownContainer';
import { SuggestUtterancesCommandBar } from 'src/luis/projects/utterances/components/SuggestUtterancesCommandBar';
import { SuggestUtterancesTable } from 'src/luis/projects/utterances/components/SuggestUtterancesTable/SuggestUtterancesTable';
import { SuggestUtterancesFilter, SuggestUtterancesToolbar } from 'src/luis/projects/utterances/components/SuggestUtterancesToolbar';
import { useSuggestedUtterances } from 'src/luis/projects/utterances/hooks/useSuggestedUtterances';
import { SuggestedUtteranceObservable } from 'src/luis/projects/utterances/utils/generateSuggestedUtterancesObservables';
import { isMLPrediction } from 'src/luis/projects/utterances/utils/utteranceUtils';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { CreateEntityModal } from 'src/luis/verticals/createEntityModal/CreateEntityModal';

type SubmitMethodType = 'commandBar' | 'utteranceRow';

/**
 * The page where endpoint utterances are displayed
 * and possibly modified
 */
export const ReviewEndpointUtterancesPage = observer(() => {
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const [localUIStore] = React.useState(() => new ReviewEndpointUtterancesUIStore());
    const eventLogger = React.useRef(getEventLogger('ReviewEndpointUtterances')).current;
    const { createTracker } = useImperativeProgressTracker();
    const { selectedItems, selection } = useSelection<SuggestedUtteranceObservable>();
    const { isLoading: areSuggestedUtterancesLoading, suggestedUtterancesObservables, reload } = useSuggestedUtterances(
        localUIStore.currentFilter
    );
    const { isLoading: areEntitiesLoading, entities } = useEntities();
    const { isLoading: areIntentLoading, intents } = useIntents();
    const isLoading = areSuggestedUtterancesLoading || areEntitiesLoading || areIntentLoading;

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const rolesArr = entities.map(entity => entity.roles);
    const roles: EntityRole[] = flatten(rolesArr);

    const discardMethodRef = React.useRef<SubmitMethodType>();

    const getUtteranceById = (id: LuisId) => suggestedUtterancesObservables.find(u => u.id === id).store;

    const {
        addLabel,
        batchConfirmPredictions,
        changePredictedIntent,
        confirmPredictions,
        onEntityCreated,
        removeLabels
    } = useReviewEndpointLocalLabelingActions({
        allItems: suggestedUtterancesObservables,
        selectedItems,
        localUIStore,
        getUtteranceById
    });

    const modalMessages = {
        title: localize(reviewEndpointMessages.unsavedLabelsModalTitle),
        description: localize(reviewEndpointMessages.unsavedLabelsModalDesc),
        leaveButtonText: localize(reviewEndpointMessages.unsavedLabelsModalContinueBtn),
        cancelButtonText: localize(globalMessages.cancel)
    };

    const filteredEntities = entities.filter(
        e => e.typeId !== ModelType.List && e.typeId !== ModelType.PatternAny && e.typeId !== ModelType.Regex
    );
    const { isRTL } = rootStore.currentLuisAppStore;

    const predictionsBtnDisabled = computed(() => {
        const items = selectedItems.length > 0 ? selectedItems : suggestedUtterancesObservables;

        return items.reduce((acc, val) => acc && !val.computed.get()[1].labels.some(isMLPrediction), true);
    }).get();
    const hasUnconfirmedPredictions = computed(() =>
        selectedItems.reduce((acc, item) => acc || item.computed.get()[1].labels.some(isMLPrediction), false)
    ).get();
    const areActionsDisabled = computed(() =>
        selectedItems.length ? selectedItems.some(item => localUIStore.inFlightUtteranceIds.has(item.id)) : false
    ).get();

    // If no filter is selected, set the first intent as the active filter.
    React.useEffect(() => {
        if (intents.length > 0 && localUIStore.currentFilter === undefined) {
            localUIStore.setCurrentFilter({
                filterId: intents[0].id,
                filterName: intents[0].name,
                filterType: 'intents'
            });
        }
    }, [intents]);

    const createRequestTracker = (ids: LuisId[]) =>
        createTracker({
            onDone: () => {
                transaction(() => {
                    localUIStore.deleteFromInFlightUtterances(ids);
                    localUIStore.deleteFromDirtyUtterances(ids);
                    selection.setAllSelected(false);
                });
            },
            onRunning: () => localUIStore.addToInFlightUtterances(ids)
        });

    const confirmUnsavedLabels = async (cb: () => void) => {
        if (localUIStore.isDirty) {
            if (await confirmLeave(modalMessages)) {
                cb();
                localUIStore.resetDirtyUtterances();
            }
        } else {
            cb();
        }
    };

    const handleAddSynonymToSublist = (entityId: LuisId, sublistName: string, value: string) =>
        dispatcher.dispatch('addSynonymToSublist', {
            id: entityId,
            name: sublistName,
            value
        });

    const handleCreateSublist = (entityId: LuisId, sublistName: string, values: string[]) =>
        dispatcher.dispatch('addSublistToListEntity', {
            id: entityId,
            sublist: { name: sublistName, values }
        });

    const handleSubmit = (method: SubmitMethodType, utteranceId?: LuisId) => {
        const utteranceIds = utteranceId ? [utteranceId] : selectedItems.map(item => item.id);
        const utterances = utteranceIds.map(getUtteranceById);

        switch (method) {
            case 'commandBar':
                eventLogger.log('SaveUtterancesUsingCommandBar', { utterancesLength: utteranceIds.length });
                break;

            case 'utteranceRow':
                eventLogger.log('SaveUtterancesUsingUtteranceRow', { utterancesLength: utteranceIds.length });
                break;

            default:
                break;
        }

        dispatcher.dispatch('addSuggestedUtterancesToIntent', {
            utterances: utterances.map(({ utteranceStore }) => ({
                id: utteranceStore.id,
                labels: utteranceStore.labeledEntities,
                predictedIntentId: utteranceStore.labeledIntentId
            })),
            tracker: createRequestTracker(utteranceIds),
            toastData: {
                running: { title: localize(reviewEndpointMessages.addToastStart) },
                completed: { title: localize(reviewEndpointMessages.addToastSuccess) },
                failed: { title: localize(reviewEndpointMessages.addToastFailed) }
            }
        });
    };

    /**
     * A callback function to delete a suggested utterance
     */
    const handleDiscard = () => {
        const utteranceIds = localUIStore.toBeDiscardedUtteranceIds;
        localUIStore.setToBeDiscardedUtteranceIds([]);

        switch (discardMethodRef.current) {
            case 'commandBar':
                eventLogger.log('DiscardUtterancesUsingCommandBar', { utterancesLength: utteranceIds.length });
                break;

            case 'utteranceRow':
                eventLogger.log('DiscardUtterancesUsingUtteranceRow', { utterancesLength: utteranceIds.length });
                break;

            default:
                break;
        }
        discardMethodRef.current = undefined;

        dispatcher.dispatch('deleteSuggestedUtterances', {
            utteranceIds,
            tracker: createRequestTracker(utteranceIds),
            generateMultiFailureMessage: (failedUtteranceIds: LuisId[]) =>
                localize(reviewEndpointMessages.multiFailureMessage, { count: failedUtteranceIds.length }),
            toastData: {
                running: { title: localize(reviewEndpointMessages.deleteToastStart) },
                completed: { title: localize(reviewEndpointMessages.deleteToastSuccess) },
                failed: { title: localize(reviewEndpointMessages.deleteToastFailed) }
            }
        });
    };

    const handleRequestDiscard = (method: SubmitMethodType, utteranceId?: LuisId) => {
        const utteranceIds = utteranceId ? [utteranceId] : selectedItems.map(item => item.id);

        discardMethodRef.current = method;
        localUIStore.setToBeDiscardedUtteranceIds(utteranceIds);
    };

    const handleRequestCreateEntity = (entityName: string) =>
        transaction(() => {
            localUIStore.setCreateNewEntityName(entityName);
            localUIStore.setIsCreateEntityModalOpen(true);
        });

    const handleRefresh = async () => await confirmUnsavedLabels(reload);

    const handleChangeFilter = async (filter: SuggestUtterancesFilter) =>
        await confirmUnsavedLabels(() => {
            eventLogger.log('FilterSelectionChange');
            localUIStore.setCurrentFilter(filter);
        });

    return (
        <>
            <Prompt when={localUIStore.isDirty} message={JSON.stringify(modalMessages)} />

            <Page pageName="ReviewEndpointUtterancesPage" size="container-fluid" title={localize(reviewEndpointMessages.pageTitle)}>
                <EditableTitle
                    title={localize(reviewEndpointMessages.pageTitle)}
                    description={localize(reviewEndpointMessages.description)}
                />
                {!canWrite && <LimitedAccessToFeaturesMessageBar displayedUserRole={displayedUserRole} />}
                <SuggestUtterancesToolbar
                    roles={roles}
                    intents={intents}
                    onRefresh={handleRefresh}
                    entities={filteredEntities}
                    onChangeFilter={handleChangeFilter}
                    currentFilter={localUIStore.currentFilter}
                />

                <SuggestUtterancesCommandBar
                    hasUnconfirmedPredictions={hasUnconfirmedPredictions}
                    areActionsDisabled={areActionsDisabled}
                    onDiscard={() => handleRequestDiscard('commandBar')}
                    onSubmit={() => handleSubmit('commandBar')}
                    isConfirmPredictionsDisabled={predictionsBtnDisabled}
                    onConfirmEntityPredictions={batchConfirmPredictions}
                    selectedItems={selectedItems}
                    userRole={userRole}
                    displayedUserRole={displayedUserRole}
                />

                <SuggestUtterancesTable
                    selection={selection}
                    reviewEndpointUtteranceUIStore={localUIStore}
                    isRTL={isRTL}
                    computedSuggestedUtterances={suggestedUtterancesObservables}
                    onChangePredictedIntent={changePredictedIntent}
                    onLoadMore={handleRefresh}
                    isLoading={isLoading}
                    onConfirmPredictions={batchConfirmPredictions}
                    onSubmit={id => handleSubmit('utteranceRow', id)}
                    onDiscard={id => handleRequestDiscard('utteranceRow', id)}
                    userRole={userRole}
                />

                <Observer>
                    {() => (
                        <EntityDropdownContainer
                            isEntityPaneOpen={false}
                            menuStore={localUIStore.labelingStore.menuStore}
                            tokenSelectionMetadata={localUIStore.labelingStore.tokenSelectionMetadata}
                            onSetTokenSelectionMetadata={(...args) => localUIStore.labelingStore.setTokenSelectionMetadata(...args)}
                            entities={entities.filter(e => isMachineLearned(e.typeId))}
                            utteranceLabels={
                                suggestedUtterancesObservables
                                    .find(u => u.id === localUIStore.labelingStore.menuStore.currentUtteranceId)
                                    ?.computed.get()[1].labels || []
                            }
                            onOpenEntityPane={noop}
                            actions={{
                                onAddLabel: addLabel,
                                onRemoveLabels: removeLabels,
                                onConfirmPrediction: (id, labels) => confirmPredictions(id, labels, true),
                                onAddSynonymToSublist: handleAddSynonymToSublist,
                                onCreateEntity: handleRequestCreateEntity,
                                onCreateSublist: handleCreateSublist
                            }}
                            config={{ enableEntityPaneAction: false, enablePrebuiltModalAction: false }}
                        />
                    )}
                </Observer>

                <DeletePromptModal
                    hidden={localUIStore.toBeDiscardedUtteranceIds.length === 0}
                    dialogContentProps={{
                        title: localize(reviewEndpointMessages.deleteUtteranceTitle),
                        subText: localize(reviewEndpointMessages.deleteUtteranceBody)
                    }}
                    onDismiss={() => localUIStore.setToBeDiscardedUtteranceIds([])}
                    onDelete={handleDiscard}
                />

                <CreateEntityModal
                    entityName={localUIStore.createNewEntityName}
                    allowedModelTypes={[ModelType.Simple, ModelType.List]}
                    hidden={!localUIStore.isCreateEntityModalOpen}
                    onSubmit={onEntityCreated}
                    onDismiss={() => localUIStore.setIsCreateEntityModalOpen(false)}
                    token={localUIStore.labelingStore.tokenSelectionMetadata?.selectedText}
                />
            </Page>
        </>
    );
});
